/* One definition of "a day", because two would be this repo's defining bug with a calendar in
 * the place of a vocabulary.
 *
 * `daysSince` began life inside `roster.ts`. The client page asks the identical question — how
 * long ago was this, in the sense a coach means — so it moved here rather than being copied.
 * A second copy of a date rule is the shape of twenty-three recorded instances: both sides look
 * right, and they disagree at exactly one boundary that nobody tests.
 */

/**
 * How many whole days ago, counted in CALENDAR DAYS rather than in elapsed hours.
 *
 * ⚠️ A COACH MEANS "YESTERDAY", NOT "23 HOURS AGO". Subtracting timestamps and dividing by
 * 86 400 000 makes a session logged last night at 23:00 read as "0 days ago" until 23:00
 * tonight, so a roster refreshed in the morning tells the coach somebody trained *today* when
 * they did not. Both sides are floored to midnight UTC first, so the answer is the number of
 * date boundaries crossed — which is the thing the word "days" means on this screen.
 */
export function daysSince(iso: string, today: Date): number | null {
  const then = new Date(iso + (iso.length === 10 ? "T00:00:00Z" : ""));
  if (Number.isNaN(then.getTime())) return null;
  const dayMs = 86_400_000;
  return Math.floor(today.getTime() / dayMs) - Math.floor(then.getTime() / dayMs);
}

/**
 * The calendar day a timestamp falls on, as `YYYY-MM-DD`.
 *
 * ⚠️ IN UTC, AND THAT IS A DECISION WITH A STATED COST. `set_logs.completed_at` is a
 * `timestamptz`; `coach_assignments.scheduled_date` is a bare `date` with no zone at all. To ask
 * "did they train on the day this was scheduled for" the two have to be compared in SOME zone,
 * and the only ones available are UTC or the *coach's* browser — and the coach's browser is the
 * wrong answer, because it would move an athlete's training day when their coach flew somewhere.
 *
 * Measured before choosing (2026-08-28, live, per identity): across all 349 logged sets the two
 * clients own, `completed_at::date` equals the parent session's `scheduled_date` on **every row
 * that has one** — 2026-08-11, 08-15, 08-17, 08-20 and 08-24…08-28 all agree exactly. So UTC is
 * faithful for this data today.
 *
 * The cost, stated rather than discovered later: an athlete training after 02:00 local in Athens
 * (23:00 UTC the day before) lands on the previous day. The real fix is the client's own
 * timezone — which `EVERFIT-MAP.md` §2 already marks as an ADD, and which `profiles` has no
 * column for, so it is REPORTED and not invented here.
 */
export function dayKey(iso: string, zone: string = coachZone()): string | null {
  // A BARE DATE is already a calendar day (`scheduled_date`) and is never moved by a zone.
  if (iso.length === 10) {
    const d = new Date(`${iso}T00:00:00Z`);
    return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
  }
  // An INSTANT (`set_logs.completed_at`) is the day it was on the COACH'S calendar — W72 step 1.
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  return localDayKey(t, zone);
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴 THE COACH'S DAY — ONE CLOCK (W72 step 1, Fable's ruling FIX B, 2026-09-25).
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * Every day rule here worked in UTC, so at 00:55 on a Friday in Athens the new This week board said
 * "Thursday 24 Sep" and marked Thursday as today — the shape that makes a coach distrust the page
 * the board exists to be trusted for. The phone reads the athlete's local day; the web now reads the
 * COACH'S local day, from ONE place:
 *
 *   • `coachZone()` — the browser's own zone (the coach's), `UTC` when it cannot be read.
 *   • `localDayKey(at, zone)` — the calendar day an instant falls on in a zone.
 *   • `calendarToday(now, zone)` — TODAY as the rest of this file's arithmetic wants it: a Date at
 *     00:00Z of the coach's LOCAL day. Everything downstream (`weekCells`, `mondayKey`, the
 *     `getUTC*` readers) keeps its UTC arithmetic and is simply handed the right day — so no reader
 *     needed rewriting, and every screen's "today" is one call.
 *
 * ⚠️ BOTH ARE INJECTABLE (`now`, `zone`) — a function that reads the clock itself cannot be asked
 * what it does at 00:55 in Athens, which is exactly the case that broke. The ruler asks it that.
 */
export function coachZone(): string {
  try {
    const z = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof z === "string" && z !== "" ? z : "UTC";
  } catch {
    return "UTC";
  }
}

export function localDayKey(at: Date, zone: string = coachZone()): string {
  try {
    // `en-CA` formats as YYYY-MM-DD; parts are read rather than the string trusted.
    const parts = new Intl.DateTimeFormat("en-CA", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" })
      .formatToParts(at);
    const get = (k: string) => parts.find((p) => p.type === k)?.value ?? "";
    const key = `${get("year")}-${get("month")}-${get("day")}`;
    return /^\d{4}-\d{2}-\d{2}$/.test(key) ? key : at.toISOString().slice(0, 10);
  } catch {
    // An unresolvable zone is UTC — a known answer, never a thrown render.
    return at.toISOString().slice(0, 10);
  }
}

export function calendarToday(now: Date = new Date(), zone: string = coachZone()): Date {
  return new Date(`${localDayKey(now, zone)}T00:00:00Z`);
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴 W112 — A TRAINING DAY IS THE DAY IT WAS SCHEDULED FOR (Fable's ruling, 2026-09-30).
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * MEASURED: a logged set's INSTANT read in the coach's zone moves a whole workout to another day for a coach far from
 * his client. William (east) saw the example client's Monday sets, logged 17:36–17:54 UTC, as TUESDAY — Monday `missed`,
 * Tuesday `done` with nothing on it (`~/ht-logs/william-0930/his-shot-2-client-calendar.jpg`). Live, 28 of the 66
 * finished training sessions that carry a `scheduled_date` fall on another day read in Asia/Tokyo; 1 in Athens; 0 in UTC.
 *
 * THE RULE: a logged set belongs to its parent session's SCHEDULED day — a bare date, never moved by any zone — which is
 * also what the athlete's own phone ticks (`TrainedDays.swift` folds `workout_sessions.scheduled_date`), so the coach and
 * the client read ONE week. Only a set whose session has NO scheduled day (17 live sessions, all before 2026-08-06) falls
 * back to its instant, read in the coach's zone as TODAY is (FIX B stands). The Swift twin is
 * `CoachDashboardFacts.trainedDay`, pinned equal on the corpus.
 * ⚠️ Monday's workout done late on Tuesday now ticks MONDAY — that is the point, not a side effect.
 */
export function trainedDayKey(scheduledDate: string | null | undefined, completedAt: string | null, zone: string = coachZone()): string | null {
  if (completedAt === null) return null; // not logged — no day at all
  if (typeof scheduledDate === "string" && scheduledDate.length >= 10) {
    const day = dayKey(scheduledDate.slice(0, 10));
    if (day !== null) return day;
  }
  return dayKey(completedAt, zone);
}

/**
 * LAST TRAINED over trained days, never later than TODAY: a workout scheduled for Thursday and done early on Tuesday
 * ticks Thursday (the rule above), but on Tuesday the client trained "Today", not "Thu". `null` when there are none.
 */
export function lastTrainedDayKey(days: Iterable<string>, todayKey: string): string | null {
  let latest: string | null = null;
  for (const d of days) if (latest === null || d > latest) latest = d;
  return latest !== null && latest > todayKey ? todayKey : latest;
}

/** `YYYY-MM-DD` for a `Date`, in the same zone `dayKey` uses, so the two can be compared. */
export function dayKeyOf(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** The day `offset` days from `from`, as a key. Negative goes back. */
export function shiftDayKey(from: Date, offset: number): string {
  return new Date(from.getTime() + offset * 86_400_000).toISOString().slice(0, 10);
}
